import {AfterViewInit, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { AssignedTeamRoles } from 'app/enums/assigned-team-roles.enum';
import { AssignedTeamMemberType, AssignedTeamMemberTypeId, SuggestType } from 'app/enums/suggest.enum';
import { AssignedTeamItem, Item } from 'app/models/item.inferface';
import { UserModel } from 'app/models/user.model';
import {StakeholdersDto} from 'app/models/workflow/workflow-instance.interface';
import { AuthService } from 'app/services/auth.service';
import { ConsultantByDescriptionSuggestService } from 'app/services/suggest/consultant-by-description.suggest.service';
import { ToastService } from 'app/services/toast.service';
import { Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';

@Component({
  selector: 'app-assigned-team',
  templateUrl: './assigned-team.component.html',
  styleUrls: ['./assigned-team.component.scss']
})
export class AssignedTeamComponent implements OnInit, AfterViewInit {

  suggestType = SuggestType;
  memberType = AssignedTeamMemberType;
  memberTypeId = AssignedTeamMemberTypeId;
  assignedTeamRoles = AssignedTeamRoles;

  @Input()
  public editable: boolean;

  @Input()
  stakeholdersConfig: StakeholdersDto = null;

  @Input() onContinueObservable: Observable<void>;
  private onContinueSuscription: Subscription;

  @Output()
  assignedResponders: EventEmitter<StakeholdersDto> = new EventEmitter();

  form: UntypedFormGroup;

  loadingUsers = false;
  loadingGroups = false;
  public userGroupsResponders: Item[] = [];
  public usersResponders: Item[] = [];
  public usersReviewers: Item[] = [];
  public userGroupsReviewers: Item[] = [];
  public usersViewers: Item[] = [];
  public userGroupsViewers: Item[] = [];

  teamSelected: AssignedTeamItem[] = [];
  selectedTeamRole : AssignedTeamRoles.viewer | AssignedTeamRoles.responder | AssignedTeamRoles.reviewer 
    = AssignedTeamRoles.viewer;

  currentUser: UserModel;
  canAddTeam: boolean = false;

  constructor(
    private suggestService: ConsultantByDescriptionSuggestService,
    private authService: AuthService,
    private toast: ToastService
  ) {
    this.currentUser = this.authService.currentUser;
  }

  ngOnInit(): void {

    this.form = new UntypedFormGroup({
      user: new UntypedFormControl()
    });

    if(this.stakeholdersConfig) this.setInitialStakeholders();
    
    this.form.get('user').valueChanges
      .subscribe(value => {
        if (value && typeof value == 'object') {
          this.canAddTeam = true;
          if (!this.checkSelectablePermission(AssignedTeamRoles.viewer)) this.selectedTeamRole = AssignedTeamRoles.viewer;
          if (!this.checkSelectablePermission(AssignedTeamRoles.responder)) this.selectedTeamRole = AssignedTeamRoles.responder;
          if (!this.checkSelectablePermission(AssignedTeamRoles.reviewer)) this.selectedTeamRole = AssignedTeamRoles.reviewer;
        }else{
          this.canAddTeam = false;
        }
      })
  }

  ngAfterViewInit() {
    this.form.disable({emitEvent: false});
    if (this.editable) {
      this.form.enable({emitEvent: false});
    }
    
    this.onContinueSuscription = this.onContinueObservable?.subscribe(() => {
      setTimeout(() => {
        if(this.stakeholdersConfig) this.setInitialStakeholders();
      },500);
    });
  }

  ngOnDestroy(): void {
    this.onContinueSuscription?.unsubscribe();
  }

  setInitialStakeholders(): void {
    this.teamSelected = [];

    let userIds: number[] = [];
    let groupIds: number[] = [];

    for (const prop in this.stakeholdersConfig){

      if (prop.includes('Groups')) {
        for(let i of this.stakeholdersConfig[prop]) {
          if (!groupIds.some(id => id == i)) groupIds.push(i);
        }
      } else {
        for(let i of this.stakeholdersConfig[prop]) {
          if (!userIds.some(id => id == i)) userIds.push(i);
        }
      }
    }

    this.suggestService.getTeamById(userIds, this.memberTypeId.User)
      .pipe(tap(() => this.loadingUsers = true))
      .subscribe(response => {
        this.loadingUsers = false;
        this.setInitialUsers(response);
      },
      err => {
        this.loadingUsers = false; 
        this.toast.error(err);
      });
      
    this.suggestService.getTeamById(groupIds, this.memberTypeId.Group)
    .pipe(tap(() => this.loadingGroups = true))
      .subscribe(response => {
        this.loadingGroups = false;
        this.setInitialGroups(response);
      },
      err => {
        this.loadingGroups = false;
        this.toast.error(err);
      });
  }

  setInitialUsers(users: AssignedTeamItem[]): void {
    this.teamSelected = this.teamSelected.concat(users);

    this.usersViewers = [];
    this.usersResponders = [];
    this.usersReviewers = [];

    for(let v of this.stakeholdersConfig.viewers) {
      users.forEach(user => {
        if (user.id == v) this.usersViewers.push({id: user.id, description: user.description})
      })
    }
    for(let r of this.stakeholdersConfig.responders) {
      users.forEach(user => {
        if (user.id == r) this.usersResponders.push({id: user.id, description: user.description})
      })
    }
    for(let rv of this.stakeholdersConfig.reviewers) {
      users.forEach(user => {
        if (user.id == rv) this.usersReviewers.push({id: user.id, description: user.description})
      })
    }
  }

  setInitialGroups(groups: AssignedTeamItem[]): void {
    this.teamSelected = this.teamSelected.concat(groups);

    this.userGroupsViewers = [];
    this.userGroupsResponders = [];
    this.userGroupsReviewers = [];
    
    for(let v of this.stakeholdersConfig.viewerGroups) {
      groups.forEach(group => {
        if (group.id == v) this.userGroupsViewers.push({id: group.id, description: group.description})
      })
    }
    for(let r of this.stakeholdersConfig.responderGroups) {
      groups.forEach(group => {
        if (group.id == r) this.userGroupsResponders.push({id: group.id, description: group.description})
      })
    }
    for(let rv of this.stakeholdersConfig.reviewerGroups) {
      groups.forEach(group => {
        if (group.id == rv) this.userGroupsReviewers.push({id: group.id, description: group.description})
      })
    }
  }

  checkPermission(item: AssignedTeamItem, permission: 'view' | 'answer' | 'review'): boolean {
    switch(permission) {
      case 'view':
        if (item.type == this.memberType.User) return !!this.usersViewers.find(x => x.id == item.id);
        if (item.type == this.memberType.Group) return !!this.userGroupsViewers.find(x => x.id == item.id);
        break;
      case 'answer':
        if (item.type == this.memberType.User) return !!this.usersResponders.find(x => x.id == item.id);
        if (item.type == this.memberType.Group) return !!this.userGroupsResponders.find(x => x.id == item.id);
        break;
      case 'review':
        if (item.type == this.memberType.User) return !!this.usersReviewers.find(x => x.id == item.id);
        if (item.type == this.memberType.Group) return !!this.userGroupsReviewers.find(x => x.id == item.id);
        break;
    }
  }

  checkSelectedPermission(item: AssignedTeamItem): string {
    if (item.type == this.memberType.User) {
      if (!!this.usersReviewers.find(x => x.id == item.id)) return AssignedTeamRoles.reviewer;
      if (!!this.usersResponders.find(x => x.id == item.id)) return AssignedTeamRoles.responder;
      if (!!this.usersViewers.find(x => x.id == item.id)) return AssignedTeamRoles.viewer;
    }
    if (item.type == this.memberType.Group) {
      if (!!this.userGroupsReviewers.find(x => x.id == item.id)) return AssignedTeamRoles.reviewer;
      if (!!this.userGroupsResponders.find(x => x.id == item.id)) return AssignedTeamRoles.responder;
      if (!!this.userGroupsViewers.find(x => x.id == item.id)) return AssignedTeamRoles.viewer;
    }
  }

  checkSelectablePermission(permission: AssignedTeamRoles.viewer | AssignedTeamRoles.responder | AssignedTeamRoles.reviewer): boolean {
    const selectedUserPills: AssignedTeamItem[] = this.form.get('user').value;
    if (!selectedUserPills) return true;

    switch(permission) {
      case AssignedTeamRoles.viewer:
        return selectedUserPills.some(user => user.canBeViewer == false);
        break;
      case AssignedTeamRoles.responder:
        return selectedUserPills.some(user => user.canBeResponder == false);
        break;
      case AssignedTeamRoles.reviewer:
        return selectedUserPills.some(user => user.canBeReviewer == false);
        break;
    }
  }

  addMembers(): void {
    if(this.form.get('user').value){
      this.form.get('user').value.forEach(user => {
        // console.log(this.form.get('user').value);
        if (!this.teamSelected.find(x => x.id == user.id && x.type == user.type)) {
          this.teamSelected.push(user);
          this.updateStakeholderRole(user, this.selectedTeamRole);
        }
      });
    }
    this.form.get('user').setValue(null);
    this.canAddTeam = false;
  }

  updateStakeholderRole(item: AssignedTeamItem, 
    permission: AssignedTeamRoles.viewer | AssignedTeamRoles.responder | AssignedTeamRoles.reviewer, 
    $event?: any): void {

    $event?.stopPropagation();

    switch(item.type) {
      case AssignedTeamMemberType.User:
        this.usersViewers = this.removeItem(item, this.usersViewers);
        this.usersResponders = this.removeItem(item, this.usersResponders);
        this.usersReviewers = this.removeItem(item, this.usersReviewers);
        break;
      case AssignedTeamMemberType.Group:
        this.userGroupsViewers = this.removeItem(item, this.userGroupsViewers);
        this.userGroupsResponders = this.removeItem(item, this.userGroupsResponders);
        this.userGroupsReviewers = this.removeItem(item, this.userGroupsReviewers);
        break;
    }

    switch(permission) {
      case AssignedTeamRoles.viewer:
        if (item.type == this.memberType.User) this.usersViewers.push({id: item.id, description: item.description})
        if (item.type == this.memberType.Group) this.userGroupsViewers.push({id: item.id, description: item.description})
        break;
      case AssignedTeamRoles.responder:
        if (item.type == this.memberType.User) this.usersResponders.push({id: item.id, description: item.description})
        if (item.type == this.memberType.Group) this.userGroupsResponders.push({id: item.id, description: item.description})
        break;
      case AssignedTeamRoles.reviewer:
        if (item.type == this.memberType.User) this.usersReviewers.push({id: item.id, description: item.description})
        if (item.type == this.memberType.Group) this.userGroupsReviewers.push({id: item.id, description: item.description})
        break;
    }

    this.stakeholdersUpdated();
  }

  removeStakeholder(item: AssignedTeamItem): void {
    this.teamSelected.splice(this.teamSelected.indexOf(item), 1);

    if (item.type == this.memberType.User) {
      this.usersViewers = this.removeItem(item, this.usersViewers);
      this.usersResponders = this.removeItem(item, this.usersResponders);
      this.usersReviewers = this.removeItem(item, this.usersReviewers);
    }
    if (item.type == this.memberType.Group) {
      this.userGroupsViewers = this.removeItem(item, this.userGroupsViewers);
      this.userGroupsResponders = this.removeItem(item, this.userGroupsResponders);
      this.userGroupsReviewers = this.removeItem(item, this.userGroupsReviewers);
    }

    this.stakeholdersUpdated();
  }

  removeItem (item: AssignedTeamItem, array: Item[]): Item[] {
    return array.filter(value => item.id != value.id);
  }

  stakeholdersUpdated(): void {
    const newStakeholders: StakeholdersDto = {
      viewers: this.usersViewers.map(user => user.id),
      viewerGroups: this.userGroupsViewers.map(group => group.id),
      responders: this.usersResponders.map(user => user.id),
      responderGroups: this.userGroupsResponders.map(group => group.id),
      reviewers: this.usersReviewers.map(user => user.id),
      reviewerGroups: this.userGroupsReviewers.map(group => group.id)
    }
    this.assignedResponders.emit(newStakeholders);
  }

}
